import * as S from './styles';
import { ControlCardDescription, Modal } from '@/components';
import ContentList from './content-item';
import { useDbFunctions } from '@/talons';
import { useClient, useGlobals } from '@/hooks';
import ControlCardMode from './control-card-mode';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrandProps, ProductProps, CategoryProps, FilterItemProps } from '@/types';
import Item from './item';

interface FilterProps {
  categoryId?: number;
  top?: number;
  products?: ProductProps[];
  hasRadius?: boolean;
  filters: FilterItemProps[];
  setFilters: (val: FilterItemProps[]) => void;
  setCategories?: (val: CategoryProps[]) => void;
}

type FilterTypes = 'category' | 'brand';

const FilterBar = ({ products, top, categoryId, setFilters, filters = [], hasRadius = false }: FilterProps) => {
  const { scrollDirection } = useGlobals();
  const [isOpenModal, setModalState] = useState(false);
  const [localFilters, setLocalFilters] = useState<FilterItemProps[]>(filters);
  const [rawDbBrands, setRawDbBrands] = useState<BrandProps[]>([]);
  const [rawDbCategories, setRawDbCategories] = useState<CategoryProps[]>([]);

  const { getOnDB } = useDbFunctions();
  const { campanhaProdutos } = useClient();

  // Efeito para buscar dados iniciais de marcas e categorias do banco de dados ao montar o componente.
  useEffect(() => {
    const fetchData = async () => {
      const listBrands = (await getOnDB('marcas')) || [];
      const listCategories = (await getOnDB('categoria')) || [];
      setRawDbBrands(listBrands);
      setRawDbCategories(listCategories);
    };
    fetchData();
  }, [getOnDB]);

  // Memoriza e processa as categorias brutas do banco, associando produtos e ordenando-as. Também chama setCategories se a função for fornecida.
  const allCategories = useMemo(() => {
    const categoriesData =
      rawDbCategories
        ?.map((c) => {
          const brandsAssociated =
            products?.reduce((acc, p) => {
              if (p.COD_CATEGORIA === c.COD_CATEGORIA && !acc.includes(p.COD_MARCA)) {
                return [...acc, p.COD_MARCA];
              }
              return acc;
            }, [] as number[]) || [];
          return { ...c, brands: brandsAssociated };
        })
        ?.sort((a, b) => {
          if (a?.DE_CATEGORIA > b?.DE_CATEGORIA) return 1;
          if (a?.DE_CATEGORIA < b?.DE_CATEGORIA) return -1;
          return 0;
        }) || [];
    // if (setCategories) {
    //   setCategories(categoriesData);
    // }
    return categoriesData;
  }, [rawDbCategories, products]);

  // Memoriza e ordena as marcas brutas do banco.
  const allBrands = useMemo(() => {
    return (
      rawDbBrands?.sort((a, b) => {
        if (a?.DESC_MARCA > b?.DESC_MARCA) return 1;
        if (a?.DESC_MARCA < b?.DESC_MARCA) return -1;
        return 0;
      }) || []
    );
  }, [rawDbBrands]);

  // Memoriza um Set de IDs de produtos positivados da campanha ativa.
  const produtosPositivados = useMemo(() => new Set(campanhaProdutos?.ativo?.produto || []), [campanhaProdutos?.ativo?.produto]);

  // Memoriza um Set de IDs de produtos não positivados da campanha inativa.
  const produtosNaoPositivados = useMemo(
    () => new Set(campanhaProdutos?.inativo?.produto || []),
    [campanhaProdutos?.inativo?.produto]
  );

  // Memoriza os filtros locais agrupados por tipo (positivação, categoria, marca) em Sets para busca eficiente.
  const filtrosPorTipo = useMemo(
    () => ({
      positivacao: new Set(localFilters.filter((f) => f.type === 'positivacao').map((f) => f.id)),
      categoria: new Set(localFilters.filter((f) => f.type === 'category').map((f) => f.id)),
      marca: new Set(localFilters.filter((f) => f.type === 'brand').map((f) => f.id)),
    }),
    [localFilters]
  );

  // Memoriza a contagem de produtos que atendem aos filtros locais aplicados.
  const produtosFiltradosContagem = useMemo(() => {
    if (!products?.length) return 0;
    if (!localFilters?.length) return products?.length;

    return products.filter((produto) => {
      if (filtrosPorTipo.positivacao.size > 0) {
        const isPositivado = produtosPositivados.has(produto.COD_PRODUTO);
        const isNaoPositivado = produtosNaoPositivados.has(produto.COD_PRODUTO);
        const filtroPositivado = filtrosPorTipo.positivacao.has('positivou');
        const filtroNaoPositivado = filtrosPorTipo.positivacao.has('nao-positivou');

        if (filtroPositivado && !filtroNaoPositivado && !isPositivado) return false;
        if (!filtroPositivado && filtroNaoPositivado && !isNaoPositivado) return false;
        if (filtroPositivado && filtroNaoPositivado && !isPositivado && !isNaoPositivado) return false;
      }

      if (filtrosPorTipo.categoria.size > 0 && !filtrosPorTipo.categoria.has(produto.COD_CATEGORIA)) {
        return false;
      }

      if (filtrosPorTipo.marca.size > 0 && !filtrosPorTipo.marca.has(produto.COD_MARCA)) {
        return false;
      }
      return true;
    }).length;
  }, [products, filtrosPorTipo, produtosPositivados, produtosNaoPositivados]);

  // Efeito para sincronizar os filtros locais com os filtros externos (prop `filters`) quando o modal é fechado.
  useEffect(() => {
    if (!isOpenModal) {
      setLocalFilters(filters);
    }
  }, [isOpenModal, filters]);

  // Memoriza as contagens de produtos por ID de categoria e ID de marca. Isso evita recalcular essas contagens repetidamente.
  const categoryAndBrandProductCounts = useMemo(() => {
    if (!products?.length) return { categoryCounts: {}, brandCounts: {} };
    const categoryCounts: { [key: string]: number } = {};
    const brandCounts: { [key: string]: number } = {};
    for (const product of products) {
      categoryCounts[product.COD_CATEGORIA] = (categoryCounts[product.COD_CATEGORIA] || 0) + 1;
      brandCounts[product.COD_MARCA] = (brandCounts[product.COD_MARCA] || 0) + 1;
    }
    return { categoryCounts, brandCounts };
  }, [products]);

  // Callback memorizada para montar a lista de itens de filtro (categorias ou marcas) com suas respectivas contagens e estado de seleção.
  const mountList = useCallback(
    (list: any[], type: FilterTypes): FilterItemProps[] => {
      if (!list?.length) return [];

      const countsToUse =
        type === 'brand' ? categoryAndBrandProductCounts.brandCounts : categoryAndBrandProductCounts.categoryCounts;

      return list.reduce((acc, c) => {
        const keyId = type === 'brand' ? 'ID_MARCA' : 'COD_CATEGORIA';
        const id = c?.[keyId];
        const count = countsToUse?.[id] || 0;

        if (!count) return acc;

        const maped = {
          id,
          type,
          count,
          brands: c.brands, // For categories, `c.brands` comes from `allCategories`
          checked: localFilters?.some((f) => f.id === id && f.type === type),
          title: c?.[type === 'brand' ? 'DESC_MARCA' : 'DE_CATEGORIA'],
        } as FilterItemProps;

        return [...acc, maped];
      }, []);
    },
    [localFilters, categoryAndBrandProductCounts]
  );

  // Memoriza a lista de categorias mapeadas para exibição, incluindo descendentes se um categoryId for fornecido.
  const mapedCategories: FilterItemProps[] = useMemo(() => {
    const findDescendants = (items: CategoryProps[], fatherID: number) => {
      let descendants = [] as CategoryProps[];
      const findChildren = (id: number) => {
        const children = items.filter((item) => item.COD_CATEGORIA_PAI === id);
        children.forEach((child) => {
          descendants.push(child);
          findChildren(child.COD_CATEGORIA);
        });
      };
      findChildren(fatherID);
      return descendants;
    };

    const listCategories = categoryId ? findDescendants(allCategories || [], categoryId) : allCategories;

    return mountList(listCategories, 'category');
  }, [allCategories, categoryId, mountList]);

  // Memoriza a lista de marcas mapeadas para exibição, filtrando-as com base nas categorias visíveis e nos filtros de categoria ativos.
  const mapedBrands = useMemo(() => {
    if (!allBrands?.length) return [];

    let brandsToShow = allBrands.filter((b) => mapedCategories.some((c) => c.brands?.includes(b.ID_MARCA)));

    const activeCategoryFilters = localFilters.filter((f) => f.type === 'category');
    if (activeCategoryFilters.length > 0) {
      const brandsFromSelectedCategories = new Set<number>();
      activeCategoryFilters.forEach((catFilter) => {
        catFilter.brands?.forEach((brandId) => brandsFromSelectedCategories.add(brandId));
      });

      if (brandsFromSelectedCategories.size > 0) {
        brandsToShow = brandsToShow.filter((brand) => brandsFromSelectedCategories.has(brand.ID_MARCA));
      }
    }
    return mountList(brandsToShow, 'brand');
  }, [allBrands, localFilters, mapedCategories, mountList]);

  // Callback memorizada para lidar com a seleção/desseleção de um item de filtro. Atualiza os filtros locais e, se o modal não estiver aberto, os filtros externos.
  const handleList = useCallback(
    (filtro: FilterItemProps) => {
      const { id, checked, type } = filtro;
      let updatedFilters = [...localFilters];

      if (!checked) {
        updatedFilters.push(filtro);
      } else {
        updatedFilters = localFilters.filter((f) => !(f.type === type && f.id === id));

        if (type === 'category') {
          const deselectedCategoryBrands = filtro.brands || [];
          if (deselectedCategoryBrands.length > 0) {
            const brandsFromOtherSelectedCategories = new Set<number>();
            updatedFilters.forEach((lf) => {
              if (lf.type === 'category' && lf.brands) {
                lf.brands.forEach((brandId) => brandsFromOtherSelectedCategories.add(brandId));
              }
            });

            const brandsToRemoveFromFilter = deselectedCategoryBrands.filter(
              (brandId) => !brandsFromOtherSelectedCategories.has(brandId)
            );

            if (brandsToRemoveFromFilter.length > 0) {
              updatedFilters = updatedFilters.filter(
                (f) => !(f.type === 'brand' && brandsToRemoveFromFilter.includes(Number(f.id)))
              );
            }
          }
        }
      }
      setLocalFilters(updatedFilters);
      setFilters(updatedFilters);
    },
    [localFilters, isOpenModal, setFilters]
  );

  // Callback para limpar todos os filtros locais.
  const clearFilters = useCallback(() => setLocalFilters([]), []);

  // Callback para salvar os filtros locais nos filtros externos e fechar o modal.
  const saveFilters = useCallback(() => {
    setFilters(localFilters);
    setModalState(false);
  }, [setFilters, localFilters]);

  // Memoriza a lista de filtros de campanha (positivados/não positivados) para exibição.
  const mapedCampanha: FilterItemProps[] = useMemo(() => {
    if (!campanhaProdutos) return [];

    const count = (list: number[]) => products?.reduce((acc, p) => acc + (list.includes(p.COD_PRODUTO) ? 1 : 0), 0) || 0;
    const isChecked = (id: string) => localFilters?.some((f) => f.id === id && f.type === 'positivacao');

    const positivadosFilter = {
      id: 'positivou',
      type: 'positivacao',
      title: 'Positivados',
      checked: isChecked('positivou'),
      count: count(campanhaProdutos?.ativo?.produto || []),
    } as FilterItemProps;

    const naoPositivadosFilter = {
      id: 'nao-positivou',
      type: 'positivacao',
      title: 'Não Positivados',
      checked: isChecked('nao-positivou'),
      count: count(campanhaProdutos?.inativo?.produto || []),
    } as FilterItemProps;

    return [positivadosFilter, naoPositivadosFilter].filter((filter) => filter.count > 0);
  }, [localFilters, campanhaProdutos, products]);

  return (
    <>
      <S.RowFilter>
        <S.ColLeft>
          <S.FilterButton onClick={() => setModalState(true)} data-test="filterbar-button-open-modal">
            <S.ContentFilterIcon>
              <S.FilterIcon size={20} />
              {!!filters.length && ( // Show count from persisted filters
                <S.Notification right={40}>{filters.length}</S.Notification>
              )}
            </S.ContentFilterIcon>
            Filtrar
          </S.FilterButton>
        </S.ColLeft>

        <S.ColRight>
          <ControlCardDescription />
          <ControlCardMode />
        </S.ColRight>
      </S.RowFilter>

      {!!mapedCategories.length && (
        <S.Content top={scrollDirection !== 'down' ? top : -100}>
          <S.RowCategories $hasRadius={hasRadius}>
            {mapedCategories.map((c) => (
              <Item key={`${c.type}-${c.id}`} item={c} handleList={handleList} />
            ))}
          </S.RowCategories>
        </S.Content>
      )}

      <Modal
        fullHeight
        showDragHeader={false}
        id="filter-bar"
        direction="right"
        isScrollable={false}
        isOpen={isOpenModal}
        closeModal={() => setModalState(false)}
        showCloseIcon={false}
      >
        <S.BgBody>
          <S.ModalHeader>
            <S.ArrowBackContent>
              <S.ArrowBack size={24} onClick={() => setModalState(false)} data-test="filterbar-button-close-modal" />
            </S.ArrowBackContent>

            <S.ModalTitleContent>
              <S.ModalTitle>
                Filtrar
                {!!localFilters.length && <S.Notification>{localFilters.length}</S.Notification>}
              </S.ModalTitle>
            </S.ModalTitleContent>

            <S.ClearFilter onClick={clearFilters} data-test="filterbar-button-clean-filters">
              Limpar
            </S.ClearFilter>

            <S.BorderedHeader />
          </S.ModalHeader>

          <S.ContentModal>
            <ContentList showDivisor title="Categorias" handleList={handleList} filters={mapedCategories} />

            <ContentList showDivisor title="Marcas" filters={mapedBrands} handleList={handleList} />

            {!!campanhaProdutos && mapedCampanha.length > 0 && (
              <ContentList title="Positivação" filters={mapedCampanha} handleList={handleList} />
            )}
          </S.ContentModal>

          <S.ContentFooter>
            <S.ContentCount>
              <S.Count>{produtosFiltradosContagem}</S.Count>
              <S.CountLabel>{produtosFiltradosContagem === 1 ? 'Produto encontrado' : 'Produtos encontrados'}</S.CountLabel>
            </S.ContentCount>

            <S.Button onClick={saveFilters} data-test="filterbar-button-save-filters">
              Ver resultado
            </S.Button>
          </S.ContentFooter>
        </S.BgBody>
      </Modal>
    </>
  );
};

export default FilterBar;
